import { useState } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Upload, CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";

const fmtDate = (d) => (d ? d.split("-").reverse().join(".") : "-");

const PreviewRow = ({ r, formatPrice }) => (
  <tr className={`border-b text-xs ${r.valid ? "" : "bg-red-50"}`} data-testid={`transfer-preview-row-${r.row}`}>
    <td className="p-2 text-grey">{r.row}</td>
    <td className="p-2">
      {r.valid ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
    </td>
    <td className="p-2 font-medium">{r.member_name || "-"}</td>
    <td className="p-2">{r.package_name || "-"}</td>
    <td className="p-2 text-center">{r.prior_used} / {r.remaining} / {r.total_sessions}</td>
    <td className="p-2 text-right">{formatPrice(r.price)}</td>
    <td className="p-2 text-right">{formatPrice(r.prior_paid)}</td>
    <td className="p-2 whitespace-nowrap">{fmtDate(r.sale_date)} → {fmtDate(r.end_date)}</td>
    <td className="p-2">
      {r.errors.map((e) => <p key={e} className="text-red-700">{e}</p>)}
      {r.warnings.map((w) => <p key={w} className="text-amber-700">{w}</p>)}
    </td>
  </tr>
);

export const TransferImportDialog = ({ open, onOpenChange, businessId, onImported }) => {
  const { formatPrice } = useCurrency();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setPreview(null); setBusy(false); };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/transfer-packages/template`, { credentials: "include" });
      if (!res.ok) throw new Error((await res.json()).detail || "Şablon indirilemedi");
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = "devir_paketleri_sablon.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/businesses/${businessId}/transfer-packages/preview`, { method: "POST", credentials: "include", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Dosya okunamadı");
      setPreview(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    const rows = preview.rows.filter((r) => r.valid);
    setBusy(true);
    try {
      const res = await fetch(`${API}/businesses/${businessId}/transfer-packages/import`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rows }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Aktarım başarısız");
      toast.success(`${data.created_count} devir paketi aktarıldı${data.failed_count ? `, ${data.failed_count} satır hatalı` : ""}`);
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="transfer-import-dialog">
        <DialogHeader>
          <DialogTitle>Toplu Devir Aktarımı</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="p-4 rounded-xl border border-slate-200">
              <p className="font-medium text-navy text-sm">1. Şablonu indirin ve doldurun</p>
              <p className="text-xs text-grey mt-1">Üye No → Telefon → E-posta sırasıyla eşleşir. Paket adı sistemdekiyle aynı olmalı.</p>
              <Button variant="outline" size="sm" className="mt-3 rounded-lg" onClick={downloadTemplate} data-testid="transfer-template-download-btn">
                <Download className="w-4 h-4 mr-1" /> Excel Şablonu İndir
              </Button>
            </div>
            <label className="p-4 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/50 cursor-pointer hover:bg-violet-50 transition-colors">
              <p className="font-medium text-navy text-sm">2. Dosyayı yükleyin (.xlsx / .csv)</p>
              <p className="text-xs text-grey mt-1">Kaydetmeden önce satırları kontrol edebilirsiniz.</p>
              <span className="inline-flex items-center gap-1 mt-3 text-sm text-violet-700 font-medium">
                {busy && !preview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Dosya seç
              </span>
              <input type="file" accept=".xlsx,.csv" className="hidden" data-testid="transfer-file-input"
                onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>

          {preview && (
            <>
              <div className="flex flex-wrap items-center gap-2" data-testid="transfer-preview-summary">
                <Badge className="bg-slate-100 text-slate-700">Toplam {preview.total}</Badge>
                <Badge className="bg-emerald-100 text-emerald-700">Geçerli {preview.valid_count}</Badge>
                <Badge className="bg-red-100 text-red-700">Hatalı {preview.error_count}</Badge>
                {preview.error_count > 0 && (
                  <span className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Hatalı satırlar atlanır.</span>
                )}
              </div>
              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full">
                  <thead className="bg-slate-50 text-xs text-grey">
                    <tr>
                      <th className="p-2 text-left">Satır</th><th className="p-2"></th><th className="p-2 text-left">Üye</th>
                      <th className="p-2 text-left">Paket</th><th className="p-2">Yapılan / Kalan / Toplam</th>
                      <th className="p-2 text-right">Fiyat</th><th className="p-2 text-right">Önceden Ödenen</th>
                      <th className="p-2 text-left">Satış → Bitiş</th><th className="p-2 text-left">Durum</th>
                    </tr>
                  </thead>
                  <tbody>{preview.rows.map((r) => <PreviewRow key={r.row} r={r} formatPrice={formatPrice} />)}</tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-xl" onClick={reset} data-testid="transfer-preview-reset-btn">Vazgeç</Button>
                <Button className="bg-navy hover:bg-navy/90 rounded-xl" disabled={busy || preview.valid_count === 0} onClick={confirmImport} data-testid="transfer-import-confirm-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {preview.valid_count} Paketi Aktar
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
